<template>
  <v-row no-gutters>
    <v-col>
      <div
        id="call-center-list"
        :class="{ 'page-fullscreen': pageStore.isFullScreen }"
      >
        <v-card
          tile
          :class="$vuetify.breakpoint.smAndDown ? 'rounded-xl' : 'rounded-t-xl'"
        >
          <PageHeader>
            <template #action>
              <v-btn
                rounded
                color="warning"
                class="ml-3"
                :loading="loadingDownload"
                @click="callCenterOrderAndLinesToExcel()"
                >İndir</v-btn
              >
            </template>
          </PageHeader>
          <v-data-table
            loader-height="2"
            :headers="tableColumns"
            :items="tableItems"
            :loading="tableLoading"
            :height="pageStore.tableHeight"
            :options.sync="tableOptions"
            :server-items-length="tableTotalRec"
            :disable-items-per-page="true"
            :mobile-breakpoint="100"
            :items-per-page="20"
            fixed-header
            hide-default-footer
          >
            <!-- Load More -->
            <template v-slot:body.append v-if="loadMoreLoading">
              <tr>
                <td :colspan="tableColumns.length">
                  <div class="text-center" v-intersect.quiet="loadMore">
                    <v-progress-circular
                      v-if="tableItems.length > 0"
                      indeterminate
                      color="primary"
                    ></v-progress-circular>
                  </div>
                </td>
              </tr>
            </template>

            <!-- Arama Alanı -->
            <template v-slot:body.prepend>
              <tr class="data-table-body-search">
                <!-- Sipariş Durum -->
                <td class="pa-0">
                  <v-select
                    v-model="tableSearchOrder"
                    :prepend-inner-icon="icons.mdiMagnify"
                    persistent-placeholder
                    :items="orderStatusItems"
                    placeholder="Ara"
                    filled
                    class="rounded-0 elevation-0"
                    hide-details
                    attach
                    clearable
                    :menu-props="{ bottom: true, offsetY: true }"
                  />
                </td>

                <!-- Sipariş No -->
                <td class="pa-0">
                  <v-text-field
                    v-model="tableSearchRefno"
                    :prepend-inner-icon="icons.mdiMagnify"
                    persistent-placeholder
                    clearable
                    placeholder="Ara"
                    filled
                    class="rounded-0 elevation-0"
                    hide-details
                  />
                </td>

                <!-- Bayi Şube -->
                <td class="pa-0">
                  <v-text-field
                    v-model="tableSearchOffice"
                    :prepend-inner-icon="icons.mdiMagnify"
                    persistent-placeholder
                    placeholder="Ara"
                    filled
                    class="rounded-0 elevation-0"
                    hide-details
                    clearable
                  />
                </td>

                <!-- Müşteri Bilgileri -->
                <td class="pa-0">
                  <v-text-field
                    v-model="tableSearchCustomer"
                    :prepend-inner-icon="icons.mdiMagnify"
                    persistent-placeholder
                    clearable
                    placeholder="Ara"
                    filled
                    class="rounded-0 elevation-0"
                    hide-details
                  />
                </td>

                <!-- Boş -->
                <td class="pa-0" v-for="(item, i) in tableEmptySearch" :key="i">
                  <v-text-field
                    filled
                    disabled
                    class="rounded-0 elevation-0"
                    hide-details
                  />
                </td>
              </tr>
            </template>

            <!-- Sipariş durum -->
            <template #[`item.ordstate`]="{ item }">
              <OrderStatus
                :value="item.ordstate"
                :color="item.orderstatecolor"
                :id="item.id"
                :items="orderStatusItems"
                @tableFetch="tableFetch()"
              />
            </template>

            <!-- Sipariş Numarası -->
            <template #[`item.refno`]="{ item }">
              <v-chip
                label
                :color="item.refno === 'HATA' ? 'error' : 'grey'"
                class="d-flex justify-center text-caption"
              >
                <span
                  class="text-caption text-xl-subtitle-2"
                  style="letter-spacing: 0.5px; color: azure"
                  >{{ item.refno }}</span
                >
              </v-chip>
            </template>

            <!-- Bayi / Şube -->
            <template #[`item.officename`]="{ item }">
              <v-chip
                color="#9391a2"
                outlined
                class="d-flex justify-center"
                label
              >
                <div class="text-capitalize" style="text-align: center">
                  {{ item.officename }}
                </div>
              </v-chip>
            </template>

            <!-- Sipariş Başlık -->
            <template #[`item.name`]="{ item }">
              <div class="text-truncate" style="max-width: 500px !important">
                <div class="text-caption text-lg-subtitle-2 white--text mt-1">
                  {{ item.name }} {{ item.surname }}
                </div>
                <div class="primary--text mb-1 mt-1" style="line-height: 1">
                  <div>
                    {{ item.address }} {{ item.state }} / {{ item.city }}
                  </div>
                  <div style="font-size: 12px !important; line-height: 2">
                    {{ formatPhone(item.tel) }}
                    <span v-if="item.email">- {{ item.email }}</span>
                  </div>
                </div>
              </div>
            </template>

            <!-- Sipariş Tarihi -->
            <template #[`item.createdatetime`]="{ item }">
              <v-chip class="d-flex justify-center" label>
                <div
                  class="text-truncate d-flex align-center"
                  style="
                    width: 130px !important;
                    text-align: center;
                    font-size: 11px !important;
                    letter-spacing: 0.5px;
                    color: azure;
                  "
                >
                  <v-icon color="" small class="mr-2">
                    {{ icons.mdiCalendarClock }}
                  </v-icon>
                  <div class="mr-1">
                    {{ item.createdatetime | moment("DD.MM.YYYY -  HH:mm") }}
                  </div>
                </div>
              </v-chip>
            </template>

            <!-- Teslim Tarihi -->
            <template #[`item.deliverydate`]="{ item }">
              <v-chip color="" class="d-flex justify-center" label>
                <div
                  class="text-truncate d-flex align-center"
                  style="
                    width: 170px !important;
                    text-align: center;
                    font-size: 11px !important;
                    letter-spacing: 0.5px;
                    color: azure;
                  "
                >
                  <v-icon color="" small class="mr-2">
                    {{ icons.mdiCalendarClock }}
                  </v-icon>
                  <div class="mr-1">
                    {{ item.deliverydate | moment("DD.MM.YYYY ") }} -
                    {{ item.deliveryhour }}
                  </div>
                </div>
              </v-chip>
            </template>
            <!-- Tutar -->
            <template #[`item.genamount`]="{ item }">
              <div class="text-caption white--text font-weight-medium">
                {{ item.genamount | currency({ symbol: "" }) }}
              </div>
            </template>

            <!-- Actions -->
            <template #[`item.actions`]="{ item }">
              <div class="d-flex justify-end">
                <v-btn
                  depressed
                  style="height: 32px !important"
                  color="chip"
                  class="text-capitalize mr-1"
                  @click="viewItem(item)"
                >
                  Görüntüle
                </v-btn>

                <v-tooltip
                  :color="item.ordstate == 1 ? 'secondary' : 'error'"
                  top
                  v-if="$ability.can('update', 'callCenter')"
                >
                  <template v-slot:activator="{ on, attrs }">
                    <v-btn
                      depressed
                      style="
                        height: 32px !important;
                        min-width: 42px !important;
                        width: 42px !important;
                      "
                      :color="item.ordstate != 1 ? 'grey' : 'chip'"
                      class="text-capitalize mr-1"
                      v-bind="attrs"
                      v-on="on"
                      @click="item.ordstate == 1 ? editItem(item) : null"
                    >
                      <v-icon small>
                        {{ icons.mdiPencil }}
                      </v-icon>
                    </v-btn>
                  </template>
                  <span>
                    <span v-if="item.ordstate == 1"> Düzenle </span>
                    <span v-else> Şube ile iletişime geçin</span>
                  </span>
                </v-tooltip>

                <v-btn
                  v-if="$ability.can('delete', 'callCenter')"
                  depressed
                  style="
                    height: 32px !important;
                    min-width: 42px !important;
                    width: 42px !important;
                  "
                  color="chip"
                  class="text-capitalize"
                  @click="deleteItem(item.id)"
                >
                  <v-icon small>
                    {{ icons.mdiDelete }}
                  </v-icon>
                </v-btn>
              </div>
            </template>
          </v-data-table>
        </v-card>
      </div>
    </v-col>
  </v-row>
</template>

<script>
import Vue from "vue";
import store from "@/store";
import { useRouter } from "@core/utils";
import { ref, watch, onMounted, onUnmounted } from "vue-demi";

// Pinia
import { useAppStore } from "@/stores/app";
import { usePageStore } from "@/stores/pageHeader";
import { useCallCenterStore } from "@/stores/callCenter";

// Utils
import { postData, formatPhone } from "@utils";
import { useDebounceFn } from "@vueuse/core";
import { useToast } from "vue-toastification/composition";

// Component
import PageHeader from "@/components/PageHeader/Index.vue";
import OrderStatus from "./OrderStatus.vue";

// Icons
import {
  mdiPencil,
  mdiCalendarClock,
  mdiBell,
  mdiMagnify,
  mdiDownload,
  mdiDelete,
} from "@mdi/js";

export default {
  components: {
    PageHeader,
    OrderStatus,
  },

  setup() {
    // Kullanıcı verileri
    const { router } = useRouter();
    const callCenterStore = useCallCenterStore();
    const pageStore = usePageStore();
    const toast = useToast();

    // Sayfa Özellikleri
    const pageSettings = () => {
      pageStore.pageTitle = "Çağrı Merkezi Takip";
      pageStore.pageIcon = mdiBell;
      pageStore.hasFullScreen = true;
      pageStore.hasDownload = false;
      pageStore.hasDateRangeAll = false;
      pageStore.hasDateRange = true;
      pageStore.hasActions = true;

      pageStore.downloadHeader = {
        "Sipariş Tipi": "mattype",
        "Sipariş No": "refno",
        "Cari Bilgileri": "customer_name",
        "Tutar (TL)": "genamount",
        Kalem: "kalem_sayisi",
        Tarih: "deliverydate",
      };
    };

    // Table
    const tableRef = ref(null);
    const tableLoading = ref(true);
    const tableItems = ref([]);
    const tableTotalRec = ref(0);

    // Table Columns
    const tableColumns = ref([
      {
        text: "Durum",
        value: "ordstate",
        align: "left px-4 px-lg-3",
        width: 200,
        sortable: false,
      },
      {
        text: "Sipariş No",
        value: "refno",
        align: "left pl-0 pr-3 px-lg-3",
        width: 130,
        sortable: false,
      },
      {
        text: "Bayi / Şube",
        align: "left pl-0 pr-3 px-lg-3",
        value: "officename",
        width: "120",
        sortable: false,
      },
      {
        text: "Müşteri Bilgileri",
        value: "name",
        align: "left min-width-400 pl-0 pr-3 px-lg-3",
        sortable: false,
      },
      {
        text: "Tutar (TL)",
        align: "right pl-0 pr-3 px-lg-3",
        value: "genamount",
        width: "100",
        sortable: false,
      },
      {
        text: "Sipariş Tarihi",
        align: "right pl-0 pr-3 px-lg-3",
        value: "createdatetime",
        width: "130px",
        sortable: false,
      },
      {
        text: "Teslimat Tarihi",
        align: "right pl-0 pr-3 px-lg-3",
        value: "deliverydate",
        width: "200px",
        sortable: false,
      },
      {
        text: "",
        align: "right px-3",
        value: "actions",
        width: "80px",
        sortable: false,
      },
    ]);

    // Table Load More
    const tablePage = ref(1);
    const loadMoreLoading = ref(false);
    const loadMore = (isIntersecting) => {
      if (isIntersecting) {
        tablePage.value++;
        tableOptions.value.page = tablePage.value;
        if (!tableLoading.value) {
          tableFetch();
        }
      }
    };

    // Tablo Arama

    const tableSearchOrder = ref("");
    const tableSearchRefno = ref("");
    const tableSearchOffice = ref("");
    const tableSearchCustomer = ref("");
    const tableEmptySearch = ref(4);
    const tableOptions = ref({
      sortBy: ["id"],
      sortDesc: [true],
    });

    const debouncedTableSearch = useDebounceFn(() => {
      tableFetch();
    }, 700);

    watch(
      [
        tableSearchOrder,
        tableSearchRefno,
        tableSearchOffice,
        tableSearchCustomer,
        () => pageStore.dateRange,
      ],
      () => {
        loadMoreLoading.value = false;
        tableLoading.value = true;
        tablePage.value = 1;
        tableOptions.value.page = 1;
        tableItems.value = [];

        debouncedTableSearch();
      }
    );

    // Ekrandaki filtreler (liste ve indirme için ortak)
    const getSearchFilter = () => ({
      order_state: tableSearchOrder.value,
      refno: tableSearchRefno.value,
      officeName: tableSearchOffice.value,
      name: tableSearchCustomer.value,
      address: tableSearchCustomer.value,
      city: tableSearchCustomer.value,
      state: tableSearchCustomer.value,
    });

    // API İstek
    const tableFetch = () => {
      tableLoading.value = true;
      if (tablePage.value === 1) {
        tableItems.value = [];
      }
      postData({
        method: "callCenterOrderList",
        options: JSON.stringify(tableOptions.value),
        startdate: pageStore.dateRange.start,
        enddate: pageStore.dateRange.end,
        search: JSON.stringify(getSearchFilter()),
      })
        .then((response) => {
          // API dönüşündeki hatalara bakıyoruz
          if (response.error) {
            throw new Error(response.msg);
          }

          // Toplam kayıt sayısı işleniyor
          tableTotalRec.value = Number(response.response.pagination.totalCount);

          // API'dan gelen veriler işleniyor

          // Responseda bir değer yoksa
          if (response.response.result.length === 0) {
            loadMoreLoading.value = false;
            return;
          }

          // Responsedan gelen veriler işleniyor
          response.response.result.map((item) => {
            tableItems.value.push({ ...item });
          });

          loadMoreLoading.value = false;
          if (tableTotalRec.value >= 20) {
            setTimeout(() => {
              loadMoreLoading.value = true;
            }, 300);
          }
          // Responstan gelen verilere göre loadmore değerlendiriliyor
        })
        .catch((e) => {
          // API hataya girdi

          Vue.swal({
            title: "Hata",
            text: e.message,
            icon: "error",
            width: 300,
            background: "#FF4C51",
            confirmButtonColor: "#A80006",
          });
        })
        .finally(() => {
          // API işlemi bittikten sonra
          tableLoading.value = false;
          startPage.value = false;
        });
    };

    // Sipariş Durumu Güncelleme
    const orderStatusItems = ref([]);
    const orderStatusItemsFetch = () => {
      postData({
        method: "callCenterOrderStatusList",
      })
        .then((response) => {
          // API dönüşündeki hatalara bakıyoruz
          if (response.error === 1) {
            throw new Error(response.msg);
          }

          for (const [key, value] of Object.entries(response.detail)) {
            orderStatusItems.value.push({
              text: value,
              value: key,
            });
          }
        })
        .catch((e) => {
          // API hataya girdi
        })
        .finally(() => {
          // API işlemi bittikten sonra
        });
    };

    // Sipariş Düzenleme
    const editItem = (item) => {
      router.push({ name: "call-center-order", params: { item } });
    };

    // Sipariş Görüntüleme
    const viewItem = (item) => {
      router.push({ name: "call-center-view", params: { item } });
    };

    // Sipariş Silme
    const deleteItem = (id) => {
      Vue.swal({
        title: "Emin misin?",
        text: "Silme işlemini onaylayın",
        icon: "question",
        showCancelButton: true,
        confirmButtonText: "Onaylıyorum",
      }).then((result) => {
        if (result.isConfirmed) {
          tableLoading.value = true;

          postData({
            method: "callCenterOrdDelete",
            id,
          })
            .then((response) => {
              if (response.error === 1) {
                throw new Error(response.msg);
              }

              toast.info(response.msg);
              tableFetch();
            })
            .catch((e) => {
              // API hataya girdi
              toast.error(e.message, {
                position: "bottom-center",
              });
              // Hata ayrıntısı
            })
            .finally(() => {
              // API işlemi bittikten sonra
              store.commit("GENERAL_DISABLED", false);
              tableLoading.value = false;
            });
        }
      });
    };

    const loadingDownload = ref(false);

    // Liste İndirme
    const callCenterOrderAndLinesToExcel = () => {
      loadingDownload.value = true;
      postData({
        method: "getCallCenterOrderAndLinesToExcel",
        startdate: pageStore.dateRange.start,
        enddate: pageStore.dateRange.end,
        filter: JSON.stringify(getSearchFilter()),
      })
        .then((response) => {
          // API dönüşündeki hatalara bakıyoruz
          if (response.error === 1) {
            throw new Error(response.msg);
          }

          window.open(response.file, "_blank");
        })
        .catch((e) => {
          // API hataya girdi
        })
        .finally(() => {
          // API işlemi bittikten sonra
          loadingDownload.value = false;
        });
    };

    watch(
      () => callCenterStore.refreshList,
      (val) => {
        if (val) {
          tableItems.value = [];
          setTimeout(() => {
            tableFetch();
          }, 300);
        }
      }
    );

    const startPage = ref(false);
    onMounted(() => {
      pageSettings();

      startPage.value = true;
      callCenterStore.$reset();
      orderStatusItemsFetch();
      tableFetch();
    });

    onUnmounted(() => {
      pageStore.pageSettingsClear();
    });

    return {
      // Utils
      pageStore,
      formatPhone,

      // Table
      tableFetch,
      tableRef,
      tableColumns,
      tableItems,
      tableLoading,
      loadMore,
      loadMoreLoading,
      tableTotalRec,
      tableEmptySearch,
      tableOptions,
      callCenterOrderAndLinesToExcel,
      loadingDownload,

      // Table Search
      tableSearchOrder,
      tableSearchRefno,
      tableSearchOffice,
      tableSearchCustomer,

      // Sipariş Durum
      orderStatusItems,

      // Actions
      viewItem,
      editItem,
      deleteItem,

      // Icons
      icons: {
        mdiPencil,
        mdiBell,
        mdiMagnify,
        mdiDownload,
        mdiDelete,
        mdiCalendarClock,
      },
    };
  },
};
</script>
